import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { usePlayer } from '../../context/PlayerContext';
import { Loader2, CheckCircle2, AlertTriangle, KeyRound, Mail, Phone, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClaimInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = usePlayer();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState({ password: '', confirmPassword: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/player/claim-invite/${token}`);
        setInvite(res.data);
      } catch (err) {
        setLoadError(err.response?.data?.detail || 'This invite link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (form.password.length < 6) {
      setSubmitError('Password must be at least 6 characters long');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/player/claim-invite/${token}`, {
        password: form.password,
        phone: form.phone || null,
      });
      // Log the player in using the returned session
      login({
        account_id: res.data.account_id,
        token: res.data.token,
        email: invite.email,
        name: invite.name,
      });
      setSuccess(true);
      setTimeout(() => navigate('/player/dashboard'), 1500);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Could not claim locker. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <Loader2 size={36} className="animate-spin" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }} />
            <p style={{ color: 'var(--text-light)' }}>Checking your invite...</p>
          </div>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
          <div className="container" style={{ maxWidth: '520px' }}>
            <div className="network-card" style={{ padding: '32px', textAlign: 'center' }} data-testid="claim-invite-error">
              <AlertTriangle size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
              <h2 style={{ marginTop: 0, marginBottom: '8px' }}>Invite Unavailable</h2>
              <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>{loadError}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/player/login" className="btn-primary">Login Instead</Link>
                <Link to="/contact" className="btn-secondary">Contact Us</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
          <div className="container" style={{ maxWidth: '520px' }}>
            <div className="network-card" style={{ padding: '32px', textAlign: 'center' }} data-testid="claim-invite-success">
              <CheckCircle2 size={56} style={{ color: '#22c55e', marginBottom: '16px' }} />
              <h2 style={{ marginTop: 0 }}>Locker Claimed!</h2>
              <p style={{ color: 'var(--text-light)' }}>Taking you to your dashboard...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content">
      <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '560px' }}>
          <div className="network-card" style={{ padding: '32px' }} data-testid="claim-invite-form">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <KeyRound size={28} style={{ color: 'var(--brand-primary)' }} />
              <div>
                <h1 style={{ margin: 0, fontSize: '1.6rem' }}>Claim Your Pacific Ghost Locker</h1>
                <p style={{ margin: '4px 0 0', color: 'var(--text-light)', fontSize: '0.9rem' }}>Set a password to activate your account</p>
              </div>
            </div>

            {/* Roster preview */}
            <div style={{
              background: 'rgba(212,175,55,0.08)',
              border: '1px solid rgba(212,175,55,0.4)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              {invite.photo_url ? (
                <img src={invite.photo_url} alt={invite.name} style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'var(--brand-primary)', color: 'black',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '1.3rem'
                }}>{invite.name?.charAt(0)?.toUpperCase() || '?'}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{invite.name}</div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', color: 'var(--text-light)', fontSize: '0.85rem', marginTop: '2px' }}>
                  {invite.team && <span><strong style={{ color: 'var(--brand-primary)' }}>{invite.team.toUpperCase()}</strong> Team</span>}
                  {invite.position && <span>&middot; {invite.position}</span>}
                  {invite.school && <span>&middot; {invite.school}</span>}
                </div>
              </div>
            </div>

            {/* Email readonly */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={14} /> Email
              </label>
              <input type="email" value={invite.email || ''} disabled data-testid="claim-invite-email" style={{ opacity: 0.8 }} />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Lock size={14} /> Password *
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  placeholder="At least 6 characters"
                  data-testid="claim-invite-password"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Lock size={14} /> Confirm Password *
                </label>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  required
                  placeholder="Re-enter password"
                  data-testid="claim-invite-confirm-password"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Phone size={14} /> Phone (optional)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 555-5555"
                  data-testid="claim-invite-phone"
                />
              </div>

              {submitError && (
                <div style={{
                  padding: '10px 14px',
                  background: 'rgba(239,68,68,0.15)',
                  border: '1px solid rgba(239,68,68,0.5)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  color: '#ef4444',
                  fontSize: '0.9rem'
                }} data-testid="claim-invite-submit-error">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
                style={{ width: '100%', padding: '12px' }}
                data-testid="claim-invite-submit"
              >
                {submitting ? 'Claiming...' : 'Claim My Locker'}
              </button>
            </form>

            <p style={{ margin: '20px 0 0', textAlign: 'center', color: 'var(--text-light)', fontSize: '0.85rem' }}>
              Already have an account? <Link to="/player/login" style={{ color: 'var(--brand-primary)' }}>Log in</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClaimInvite;
